import { useMemo, useState } from "react";

import type { DiscoveryReadinessSignal } from "../types/discoveryReadiness";

interface SignalsPanelProps {
  signals: DiscoveryReadinessSignal[];
}

const CATEGORY_LABELS: Record<string, string> = {
  strategy: "Strategy",
  operations: "Operations",
  data: "Data",
  security: "Security",
  scope: "Scope",
  delivery: "Delivery",
  unknown: "General",
};

function severityTone(severity?: string): string {
  if (severity === "error" || severity === "critical") return "hud-chip--danger";
  if (severity === "warn") return "hud-chip--warning";
  return "";
}

export function SignalsPanel({ signals }: SignalsPanelProps) {
  const [blockersOnly, setBlockersOnly] = useState(false);

  const grouped = useMemo(() => {
    const filtered = blockersOnly
      ? signals.filter((signal) => signal.severity === "error" || signal.severity === "critical")
      : signals;

    const bucket: Record<string, DiscoveryReadinessSignal[]> = {};
    filtered.forEach((signal) => {
      const key = signal.category ?? signal.severity ?? "unknown";
      bucket[key] = bucket[key] ?? [];
      bucket[key].push(signal);
    });

    return bucket;
  }, [blockersOnly, signals]);

  const groupEntries = Object.entries(grouped);

  return (
    <div className="hud-signals">
      <div className="hud-signals__header">
        <div className="hud-label">Signals</div>
        <label className="hud-toggle">
          <input
            type="checkbox"
            checked={blockersOnly}
            onChange={(event) => setBlockersOnly(event.target.checked)}
          />
          <span>Blockers only</span>
        </label>
      </div>

      {groupEntries.length === 0 ? (
        <div className="hud-empty">No signals available.</div>
      ) : (
        <div className="hud-signals__groups">
          {groupEntries.map(([group, groupSignals]) => (
            <div key={group} className="hud-signals__group">
              <div className="hud-signals__group-title">
                <span className="hud-label">{CATEGORY_LABELS[group] ?? group}</span>
                <span className="hud-subtle">{groupSignals.length} signals</span>
              </div>

              <ul className="hud-list">
                {groupSignals.map((signal) => (
                  <li key={signal.id} className="hud-list__item">
                    <div className="hud-list__title-row">
                      <span className={`hud-chip ${severityTone(signal.severity)}`}>{signal.severity}</span>
                      <span className="hud-value--muted">{signal.title}</span>
                    </div>
                    <div className="hud-subtle">{signal.message}</div>
                    {signal.evidence?.snippet ? (
                      <div className="hud-evidence">{signal.evidence.snippet}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
